/**
 * Rasterises what the customer framed in a `VisualiserImageArea` so the print partner receives the
 * cropped photo rather than the original upload.
 *
 * There is no server-side compositing for UV inserts — the visualiser is DOM-only — so the crop has
 * to happen in the browser, and the resulting `File` is what the host puts in `imageByKey`.
 */

/** The pan/zoom state a `VisualiserImageArea` holds for one image key. */
export type ImageTransform = {
  scale: number
  x: number
  y: number
}

/**
 * The untouched state: no zoom, no pan. It still needs a crop: the window shows the photo
 * `object-fit: cover`, so an unmoved photo is cut to the window's shape on screen.
 */
export const IDENTITY_IMAGE_TRANSFORM: ImageTransform = { scale: 1, x: 0, y: 0 }

/** What a rasterised crop was produced from. */
export type CroppedImageOrigin = {
  /** The customer's original upload. */
  source: File
  /** The framing that produced the crop. */
  transform: ImageTransform
}

/**
 * Remembers which upload and framing each rasterised crop came from.
 *
 * The `UvInsertSlotSelection` handed to the cart carries only the crop, so once a slot unmounts the
 * original upload would otherwise be unreachable: the customer could no longer zoom out to recover
 * what they cropped away, and re-framing would crop the already-upscaled raster a second time.
 * This registry survives unmount (it is module state, not React state) and lets the host map a
 * restored crop back to its source.
 *
 * A `WeakMap` keyed by the crop is deliberately chosen over a keyed cache: it needs no bound and no
 * eviction policy, because an entry lives exactly as long as the crop `File` that keys it. Re-framing
 * produces a new crop, the previous one leaves `croppedImageByKey` and the context selection, and
 * both it and its entry become collectable — so entries are replaced, never accumulated.
 */
const croppedImageOrigins = new WeakMap<File, CroppedImageOrigin>()

/**
 * Records the upload and framing a crop was produced from.
 *
 * @param options - Options object.
 * @param options.crop - The rasterised file.
 * @param options.source - The original upload it was cropped from.
 * @param options.transform - The framing that produced it.
 */
export function rememberCroppedImage({
  crop,
  source,
  transform,
}: {
  crop: File
  source: File
  transform: ImageTransform
}): void {
  // A fallback crop returns the original file itself; recording that would make the original look
  // like a crop of itself and permanently block it from ever being cropped.
  if (crop === source) return
  croppedImageOrigins.set(crop, { source, transform })
}

/**
 * The upload and framing a crop came from, when we produced it in this page session.
 *
 * @param file - The file to look up.
 * @returns The origin, or null when the file is an ordinary upload.
 */
export function getCroppedImageOrigin(file: File | null | undefined): CroppedImageOrigin | null {
  if (!file) return null
  return croppedImageOrigins.get(file) ?? null
}

/**
 * Whether this file is one we rasterised, and so must never be cropped again.
 *
 * @param file - The file to test.
 * @returns True when the file is a crop we produced.
 */
export function isCroppedImage(file: File | null | undefined): boolean {
  return getCroppedImageOrigin(file) != null
}

/** A rectangle in source-image pixels, ready to hand to `drawImage`. */
export type CropRect = {
  sx: number
  sy: number
  sw: number
  sh: number
}

export type ComputeCropRectOptions = {
  /** Intrinsic size of the uploaded image. */
  naturalWidth: number
  naturalHeight: number
  /** CSS pixel size of the visualiser window the image is framed in. */
  containerWidth: number
  containerHeight: number
} & ImageTransform

/**
 * Reads a UV insert config dimension as CSS pixels.
 *
 * Config sizes arrive as `"84px"`, `"84"` or `84`; percentages and keywords cannot be resolved
 * without layout, so they yield null and the caller skips cropping.
 *
 * @param value - The raw `size.width` / `size.height` value.
 * @returns The pixel length, or null when the value is not a positive pixel length.
 */
export function parseCssPixels(value: string | number | null | undefined): number | null {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : null
  if (typeof value !== "string") return null
  const match = /^\s*(-?\d*\.?\d+)\s*(px)?\s*$/.exec(value)
  if (!match) return null
  const parsed = Number.parseFloat(match[1])
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Maps the visualiser's CSS presentation back to a rectangle of source pixels.
 *
 * Derived from how `VisualiserImageArea` actually paints (see `VisualiserImageArea.vm.ts` and
 * `visualiser-image-area.module.css`):
 * - The `<img>` fills the container box exactly (`width/height: 100%`) with `object-fit: cover`,
 *   so the source is first scaled by `k = max(W/nw, H/nh)` and centred in that box.
 * - The inline style then applies `translate3d(x, y, 0) scale(scale)` about the default
 *   `transform-origin: 50% 50%`, i.e. a box point `p` lands at `c + scale*(p - c) + (x, y)`.
 *
 * Inverting both steps gives the source rectangle visible through the container's `overflow: hidden`.
 * A rectangle that runs past the source is first shrunk uniformly (so the output keeps the window's
 * aspect ratio and never distorts) and then shifted back inside the image — panning past the edge
 * therefore prints more photo rather than the window's background colour.
 *
 * @param options - Natural size, container size and the pan/zoom transform.
 * @returns The source rectangle, or null when any input is degenerate.
 */
export function computeCropRect({
  naturalWidth,
  naturalHeight,
  containerWidth,
  containerHeight,
  scale,
  x,
  y,
}: ComputeCropRectOptions): CropRect | null {
  if (
    !Number.isFinite(naturalWidth) ||
    !Number.isFinite(naturalHeight) ||
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    !Number.isFinite(scale) ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    scale <= 0
  ) {
    return null
  }

  // `object-fit: cover` scale and the painted image's offset inside the element box.
  const coverScale = Math.max(containerWidth / naturalWidth, containerHeight / naturalHeight)
  const offsetX = (containerWidth - naturalWidth * coverScale) / 2
  const offsetY = (containerHeight - naturalHeight * coverScale) / 2

  // The container's top-left corner, expressed in untransformed element-box coordinates.
  const boxLeft = containerWidth / 2 - (containerWidth / 2 + x) / scale
  const boxTop = containerHeight / 2 - (containerHeight / 2 + y) / scale

  let sw = containerWidth / (scale * coverScale)
  let sh = containerHeight / (scale * coverScale)
  let sx = (boxLeft - offsetX) / coverScale
  let sy = (boxTop - offsetY) / coverScale

  // Shrink about the rect's own centre when it asks for more source than exists, keeping aspect.
  const fit = Math.min(naturalWidth / sw, naturalHeight / sh, 1)
  if (fit < 1) {
    const centreX = sx + sw / 2
    const centreY = sy + sh / 2
    sw *= fit
    sh *= fit
    sx = centreX - sw / 2
    sy = centreY - sh / 2
  }

  sw = Math.min(Math.round(sw), naturalWidth)
  sh = Math.min(Math.round(sh), naturalHeight)
  sx = Math.min(Math.max(Math.round(sx), 0), naturalWidth - sw)
  sy = Math.min(Math.max(Math.round(sy), 0), naturalHeight - sh)

  return { sx, sy, sw, sh }
}

export type CropImageToViewOptions = {
  /** The customer's original upload. */
  file: File
  /** CSS pixel size of the visualiser window. */
  containerWidth: number
  containerHeight: number
  /** The pan/zoom the customer applied. */
  transform: ImageTransform | null | undefined
  /**
   * Ceiling on the rasterised output's width; the height follows the window's aspect ratio. The
   * actual width is the smaller of this and the cropped region's own width — see
   * `resolveOutputWidth`.
   */
  maxOutputWidth?: number
}

/** Wide enough for print at the small window sizes inserts use, without bloating the upload. */
const MAX_OUTPUT_WIDTH = 1200

/**
 * How wide to rasterise the crop.
 *
 * Two failure modes are avoided at once:
 * - A fixed width *upscales* a heavily zoomed crop. Zooming into a 300 px region and rendering it at
 *   1200 px invents three quarters of the pixels: the file is four times the size and visibly softer
 *   than the region it came from, and the print partner is handed interpolation, not photo.
 * - No ceiling at all sends a modern phone photo's full 4000 px region for an 84 px window, which is
 *   far more than the print needs and a slow canvas encode on a mid-range phone.
 *
 * So: never wider than the source region (no upscale — at worst the crop is 1:1 with the pixels the
 * customer actually framed), and never wider than the ceiling (no needless bloat). The common case,
 * a large original framed loosely, still lands on the ceiling exactly as before.
 *
 * @param options - Options object.
 * @param options.sourceWidth - Width of the cropped region in source pixels.
 * @param options.maxOutputWidth - The ceiling.
 * @returns The width to rasterise at, at least 1 pixel.
 */
export function resolveOutputWidth({
  sourceWidth,
  maxOutputWidth = MAX_OUTPUT_WIDTH,
}: {
  sourceWidth: number
  maxOutputWidth?: number
}): number {
  const ceiling = Number.isFinite(maxOutputWidth) && maxOutputWidth > 0 ? maxOutputWidth : MAX_OUTPUT_WIDTH
  const region = Number.isFinite(sourceWidth) && sourceWidth > 0 ? sourceWidth : ceiling
  return Math.max(1, Math.round(Math.min(ceiling, region)))
}

/**
 * Loads an image `File` into an `HTMLImageElement`, mirroring `cropMaskedImageWithPadding`.
 *
 * @param file - The image file to decode.
 * @returns The decoded image and the object URL to revoke once drawing is done.
 */
function loadImage(file: File): Promise<{ image: HTMLImageElement; url: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => resolve({ image, url })
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error(`Failed to load image: ${file.name}`))
    }
    image.src = url
  })
}

/**
 * Produces a `File` containing only the region the customer framed in the visualiser.
 *
 * An unmoved photo (identity or missing transform) is cropped too, to the centre the window shows.
 *
 * Falls back to the original file — never throws — when the geometry is unresolvable or the browser
 * cannot give us a canvas. Getting `file` itself back therefore means the crop failed: the keepsake
 * card treats it so and sends no photo rather than the uncropped one
 * (`PersonalisationUvInsertSlot.vm.ts`).
 *
 * @param options - The file, the visualiser window size, the transform and the output width ceiling.
 * @returns The cropped file, or the original file when cropping is not possible.
 */
export async function cropImageToView({
  file,
  containerWidth,
  containerHeight,
  transform,
  maxOutputWidth = MAX_OUTPUT_WIDTH,
}: CropImageToViewOptions): Promise<File> {
  if (typeof document === "undefined" || typeof URL?.createObjectURL !== "function") return file
  const framing = transform ?? IDENTITY_IMAGE_TRANSFORM

  let objectUrl: string | null = null
  try {
    const { image, url } = await loadImage(file)
    objectUrl = url

    const rect = computeCropRect({
      naturalWidth: image.naturalWidth || image.width,
      naturalHeight: image.naturalHeight || image.height,
      containerWidth,
      containerHeight,
      scale: framing.scale,
      x: framing.x,
      y: framing.y,
    })
    if (!rect) return file

    const outputWidth = resolveOutputWidth({ sourceWidth: rect.sw, maxOutputWidth })
    const canvas = document.createElement("canvas")
    canvas.width = outputWidth
    canvas.height = Math.max(1, Math.round((outputWidth * containerHeight) / containerWidth))
    const context = canvas.getContext("2d")
    if (!context) return file

    context.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, canvas.width, canvas.height)

    // PNG uploads keep their transparency; everything else rasterises to JPEG.
    const outputType = file.type === "image/png" ? "image/png" : "image/jpeg"
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, outputType, 0.92)
    })
    if (!blob) return file

    const baseName = file.name.replace(/\.[^.]+$/, "")
    const extension = outputType === "image/png" ? "png" : "jpg"
    return new File([blob], `${baseName}-cropped.${extension}`, { type: outputType })
  } catch (error) {
    // Never throws: hand back the original and keep the reason visible. The caller decides what a
    // failed crop means for the cart.
    console.warn("cropImageToView: falling back to the original file", error)
    return file
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  }
}
